import React, { useMemo, useState } from 'react';
import { Modal, Switch, Input, InputNumber, Select, Tooltip } from 'antd';
import { EditOutlined, CopyOutlined, CalculatorOutlined, PlusOutlined } from '@ant-design/icons';
import {
  adminService,
  Product,
  PricingItem,
  PricingItemUpdate,
} from '../../services/admin.service';
import { useNotification } from '../../hooks/useNotification';
import { resolvePricingRef, planMatchesRef, addonMatchesRef, formatRef } from '../../utils/pricingRef';

/**
 * Gestión de planes/variantes de un producto: precio USD (maestro) con MXN
 * congelado sugerido por el tipo de cambio de referencia, activar/desactivar,
 * y crear variantes nuevas duplicando una existente (nacen INACTIVE).
 */

const centsToUnits = (cents: number): number => cents / 100;
const unitsToCents = (units: number): number => Math.round(units * 100);

const priceIn = (item: PricingItem, currency: 'MXN' | 'USD'): number | undefined => {
  if (item.prices && item.prices[currency] !== undefined) return item.prices[currency];
  if (item.currency === currency) return item.priceAmount;
  return undefined;
};

interface PriceFormState {
  displayName: string;
  usd: number | null;
  mxn: number | null;
  /** solo para variante nueva */
  variantCode: string;
  /** solo para add-ons: planCodes separados por coma */
  compatiblePlans: string;
}

interface ProductPlansManagerProps {
  product: Product | null;
  plans: PricingItem[];
  addons: PricingItem[];
  fxRate: number | null;
  onClose: () => void;
  onChanged: () => void;
}

const ProductPlansManager: React.FC<ProductPlansManagerProps> = ({
  product,
  plans,
  addons,
  fxRate,
  onClose,
  onChanged,
}) => {
  const [editing, setEditing] = useState<PricingItem | null>(null);
  const [duplicating, setDuplicating] = useState<PricingItem | null>(null);
  const [form, setForm] = useState<PriceFormState | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [planForm, setPlanForm] = useState({
    planCode: '',
    planName: '',
    osType: 'GENERIC',
    osFamily: '',
    variantCode: 'STANDARD',
    billingInterval: 'MONTH',
    billingModel: 'FIXED',
    usd: null as number | null,
    mxn: null as number | null,
    vcpu: null as number | null,
    memoryMb: null as number | null,
    storageGb: null as number | null,
  });
  const [creatingAddon, setCreatingAddon] = useState(false);
  const [addonForm, setAddonForm] = useState({
    addonCode: '',
    addonName: '',
    description: '',
    usd: null as number | null,
    mxn: null as number | null,
    compatiblePlans: [] as string[],
  });
  const { success: showSuccess, error: showError } = useNotification();

  const ref = useMemo(() => (product ? resolvePricingRef(product) : null), [product]);

  const productPlans = useMemo(() => {
    if (!product || !ref) return [];
    const matching = plans.filter((p) => planMatchesRef(p, ref));
    if (product.allowedPlans && product.allowedPlans.length > 0) {
      return matching.filter((p) => product.allowedPlans!.includes(p.planCode || ''));
    }
    return matching;
  }, [product, ref, plans]);

  const productAddons = useMemo(() => {
    if (!ref) return [];
    return addons.filter((a) => addonMatchesRef(a, ref));
  }, [ref, addons]);

  const openEdit = (item: PricingItem) => {
    setDuplicating(null);
    setEditing(item);
    const usd = priceIn(item, 'USD');
    const mxn = priceIn(item, 'MXN');
    setForm({
      displayName: item.displayName || item.planName || item.addonName || '',
      usd: usd !== undefined ? centsToUnits(usd) : null,
      mxn: mxn !== undefined ? centsToUnits(mxn) : null,
      variantCode: '',
      compatiblePlans: (item.compatiblePlans || []).join(', '),
    });
  };

  const openDuplicate = (item: PricingItem) => {
    setEditing(null);
    setDuplicating(item);
    const usd = priceIn(item, 'USD');
    const mxn = priceIn(item, 'MXN');
    setForm({
      displayName: item.displayName || item.planName || '',
      usd: usd !== undefined ? centsToUnits(usd) : null,
      mxn: mxn !== undefined ? centsToUnits(mxn) : null,
      variantCode: '',
      compatiblePlans: '',
    });
  };

  const closeSubModal = () => {
    setEditing(null);
    setDuplicating(null);
    setForm(null);
  };

  const suggestMxn = () => {
    if (!form || form.usd === null || !fxRate) return;
    setForm({ ...form, mxn: Math.round(form.usd * fxRate) });
  };

  /** MXN es obligatorio: es lo que cobra MercadoPago/platform-orders hoy. */
  const validateForm = (): boolean => {
    if (!form) return false;
    if (form.mxn === null || form.mxn < 0) {
      showError('El precio MXN es obligatorio (es lo que cobra la plataforma hoy)');
      return false;
    }
    return true;
  };

  const buildPrices = (): Record<string, number> => {
    const prices: Record<string, number> = { MXN: unitsToCents(form!.mxn!) };
    if (form!.usd !== null && form!.usd >= 0) prices.USD = unitsToCents(form!.usd);
    return prices;
  };

  const handleSaveEdit = async () => {
    if (!editing || !form || !validateForm()) return;
    setIsSaving(true);
    try {
      const isAddon = editing.entityType === 'ADDON_PRICE';
      const update: PricingItemUpdate = {
        PK: editing.PK,
        SK: editing.SK,
        expectedVersion: editing.version,
        prices: buildPrices(),
        priceAmount: unitsToCents(form.mxn!),
      };
      if (isAddon) {
        update.addonName = form.displayName;
        update.compatiblePlans = form.compatiblePlans
          .split(',').map((p) => p.trim().toUpperCase()).filter(Boolean);
      } else {
        update.displayName = form.displayName;
      }
      const response = await adminService.updatePricingItem(update);
      if (!response.success) {
        showError(`Error al guardar: ${response.error}`);
        return;
      }
      showSuccess('Variante actualizada');
      closeSubModal();
      onChanged();
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDuplicate = async () => {
    if (!duplicating || !form || !validateForm()) return;
    if (!form.variantCode.trim()) {
      showError('El código de la variante es obligatorio (ej. PRO, MAX2)');
      return;
    }
    setIsSaving(true);
    try {
      const response = await adminService.createPricingVariant({
        sourcePK: duplicating.PK,
        sourceSK: duplicating.SK,
        variantCode: form.variantCode.trim().toUpperCase(),
        displayName: form.displayName,
        prices: buildPrices(),
        priceAmount: unitsToCents(form.mxn!),
      });
      if (!response.success) {
        showError(`Error al crear variante: ${response.error}`);
        return;
      }
      showSuccess('Variante creada (inactiva — actívala cuando esté lista)');
      closeSubModal();
      onChanged();
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreatePlan = async () => {
    if (!product) return;
    if (!planForm.planCode.trim() || !planForm.planName.trim()) {
      showError('Código y nombre del plan son obligatorios');
      return;
    }
    if (planForm.mxn === null || planForm.mxn < 0) {
      showError('El precio MXN es obligatorio (es lo que cobra la plataforma hoy)');
      return;
    }
    setIsSaving(true);
    try {
      const includedResources: Record<string, number> = {};
      if (planForm.vcpu !== null) includedResources.vcpu = planForm.vcpu;
      if (planForm.memoryMb !== null) includedResources.memoryMb = planForm.memoryMb;
      if (planForm.storageGb !== null) includedResources.storageGb = planForm.storageGb;

      const prices: Record<string, number> = { MXN: unitsToCents(planForm.mxn) };
      if (planForm.usd !== null && planForm.usd >= 0) prices.USD = unitsToCents(planForm.usd);

      const response = await adminService.createPricingPlan({
        typeFamily: product.typeFamily,
        planCode: planForm.planCode.trim().toUpperCase(),
        planName: planForm.planName.trim(),
        osType: planForm.osType.trim().toUpperCase() || 'GENERIC',
        osFamily: planForm.osFamily.trim().toUpperCase() || undefined,
        variantCode: planForm.variantCode.trim().toUpperCase() || undefined,
        billingInterval: planForm.billingInterval,
        billingModel: planForm.billingModel,
        priceAmount: unitsToCents(planForm.mxn),
        prices,
        ...(Object.keys(includedResources).length ? { includedResources } : {}),
      });
      if (!response.success) {
        showError(`Error al crear plan: ${response.error}`);
        return;
      }
      showSuccess('Plan creado (inactivo — actívalo cuando esté listo)');
      setCreatingPlan(false);
      onChanged();
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateAddon = async () => {
    if (!ref) return;
    if (!addonForm.addonCode.trim() || !addonForm.addonName.trim()) {
      showError('Código y nombre del add-on son obligatorios');
      return;
    }
    if (addonForm.mxn === null || addonForm.mxn < 0) {
      showError('El precio MXN es obligatorio');
      return;
    }
    setIsSaving(true);
    try {
      const prices: Record<string, number> = { MXN: unitsToCents(addonForm.mxn) };
      if (addonForm.usd !== null && addonForm.usd >= 0) prices.USD = unitsToCents(addonForm.usd);
      const response = await adminService.createPricingAddon({
        typeFamily: ref.typeFamily,
        osType: productAddons[0]?.osType || 'LINUX',
        addonCode: addonForm.addonCode.trim().toUpperCase(),
        addonName: addonForm.addonName.trim(),
        description: addonForm.description || undefined,
        priceAmount: unitsToCents(addonForm.mxn),
        prices,
        compatiblePlans: addonForm.compatiblePlans,
      });
      if (!response.success) {
        showError(`Error al crear add-on: ${response.error}`);
        return;
      }
      showSuccess('Add-on creado (inactivo — actívalo cuando esté listo)');
      setCreatingAddon(false);
      onChanged();
    } finally {
      setIsSaving(false);
    }
  };

  const planCodeOptions = useMemo(() => {
    const seen = new Set<string>();
    return productPlans
      .filter((p) => p.planCode && !seen.has(p.planCode) && seen.add(p.planCode) !== undefined)
      .map((p) => ({ value: p.planCode!, label: p.planCode! }));
  }, [productPlans]);

  const handleToggleStatus = async (item: PricingItem, active: boolean) => {
    const response = await adminService.updatePricingItem({
      PK: item.PK,
      SK: item.SK,
      expectedVersion: item.version,
      status: active ? 'ACTIVE' : 'INACTIVE',
    });
    if (!response.success) {
      showError(`Error al cambiar estado: ${response.error}`);
      return;
    }
    showSuccess(active
      ? 'Variante activada — ya es visible para clientes'
      : 'Variante desactivada — deja de venderse; contratos existentes no se afectan');
    onChanged();
  };

  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
  const fmt = (cents: number | undefined, locale: string) =>
    cents !== undefined ? `$${centsToUnits(cents).toLocaleString(locale, { minimumFractionDigits: 2 })}` : '—';

  return (
    <>
      <Modal
        title={`Planes y variantes — ${product?.name ?? ''}`}
        open={product !== null}
        onCancel={onClose}
        footer={null}
        width={860}
        destroyOnHidden
      >
        <div className="pt-2">
          <div className="flex items-start justify-between gap-4 mb-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              USD es la moneda maestra; MXN queda congelado al guardar
              {fxRate ? ` (referencia: 1 USD = $${fxRate} MXN, configurable en Precios)` : ' (configura el tipo de cambio en Precios)'}.
              Los planes, variantes y add-ons nuevos nacen inactivos.
            </p>
            <button
              onClick={() => setCreatingPlan(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg whitespace-nowrap transition-colors"
            >
              <PlusOutlined /> Crear plan desde cero
            </button>
          </div>
          {ref && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Este producto muestra al cliente los precios de:{' '}
              <span className="font-mono text-gray-700 dark:text-gray-300">{formatRef(ref)}</span>
              {' '}(igual que la pantalla de compra del cliente).
            </p>
          )}
          {productPlans.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No hay planes en el catálogo para {ref ? formatRef(ref) : 'esta familia'}.
              Usa "Crear plan desde cero" para estrenar el primero.
            </p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  {['Plan / Variante', 'USD', 'MXN', 'Activo', ''].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {productPlans.map((item) => (
                  <tr key={`${item.PK}|${item.SK}`}>
                    <td className="px-3 py-2">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {item.displayName || item.planName || item.planCode}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {item.planCode}{item.variantCode ? ` · ${item.variantCode}` : ''}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white">
                      {fmt(priceIn(item, 'USD'), 'en-US')}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {fmt(priceIn(item, 'MXN'), 'es-MX')}
                    </td>
                    <td className="px-3 py-2">
                      <Switch
                        size="small"
                        checked={item.status === 'ACTIVE'}
                        onChange={(checked) => handleToggleStatus(item, checked)}
                      />
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEdit(item)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 mr-1 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                      >
                        <EditOutlined /> Editar
                      </button>
                      <Tooltip title="Crea una variante nueva de este plan copiando su configuración">
                        <button
                          onClick={() => openDuplicate(item)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                        >
                          <CopyOutlined /> Nueva variante
                        </button>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Add-ons de la familia */}
          <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Add-ons ({productAddons.length})
              </h3>
              <button
                onClick={() => {
                  setAddonForm({ addonCode: '', addonName: '', description: '', usd: null, mxn: null, compatiblePlans: [] });
                  setCreatingAddon(true);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
              >
                <PlusOutlined /> Crear add-on
              </button>
            </div>
            {productAddons.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Esta familia no tiene add-ons.
              </p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {productAddons.map((addon) => (
                    <tr key={`${addon.PK}|${addon.SK}`}>
                      <td className="px-3 py-2">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {addon.addonName || addon.addonCode}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {addon.addonCode} · compatible: {(addon.compatiblePlans || []).join(', ') || 'todos'}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white">
                        {fmt(priceIn(addon, 'USD'), 'en-US')}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                        {fmt(priceIn(addon, 'MXN'), 'es-MX')}
                      </td>
                      <td className="px-3 py-2">
                        <Switch
                          size="small"
                          checked={addon.status === 'ACTIVE'}
                          onChange={(checked) => handleToggleStatus(addon, checked)}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => openEdit(addon)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                        >
                          <EditOutlined /> Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </Modal>

      {/* Sub-modal: editar o duplicar */}
      <Modal
        title={editing
          ? `Editar — ${editing.displayName || editing.planName || ''}`
          : `Nueva variante a partir de ${duplicating?.variantCode || duplicating?.planCode || ''}`}
        open={(editing !== null || duplicating !== null) && form !== null}
        onCancel={closeSubModal}
        onOk={editing ? handleSaveEdit : handleSaveDuplicate}
        okText={editing ? 'Guardar' : 'Crear variante (inactiva)'}
        cancelText="Cancelar"
        confirmLoading={isSaving}
        destroyOnHidden
      >
        {form && (
          <div className="pt-4">
            {duplicating && (
              <div className="mb-4">
                <label className={labelClass}>Código de la variante (único dentro del plan)</label>
                <Input
                  value={form.variantCode}
                  onChange={(e) => setForm({ ...form, variantCode: e.target.value })}
                  placeholder="ej. PRO, MAX2, ANUAL"
                />
              </div>
            )}
            <div className="mb-4">
              <label className={labelClass}>Nombre visible</label>
              <Input
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-2">
              <div>
                <label className={labelClass}>Precio USD (maestro)</label>
                <InputNumber
                  className="w-full"
                  min={0}
                  precision={2}
                  value={form.usd}
                  onChange={(value) => setForm({ ...form, usd: value })}
                />
              </div>
              <div>
                <label className={labelClass}>Precio MXN (congelado)</label>
                <div className="flex gap-2">
                  <InputNumber
                    className="flex-1"
                    min={0}
                    precision={2}
                    value={form.mxn}
                    onChange={(value) => setForm({ ...form, mxn: value })}
                  />
                  <Tooltip title={fxRate ? `Calcular con 1 USD = $${fxRate} MXN` : 'Configura el tipo de cambio en Precios'}>
                    <button
                      onClick={suggestMxn}
                      disabled={!fxRate || form.usd === null}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 disabled:opacity-40"
                    >
                      <CalculatorOutlined />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
            {editing?.entityType === 'ADDON_PRICE' && (
              <div className="mb-4">
                <label className={labelClass}>Planes compatibles (separados por coma; vacío = todos)</label>
                <Input
                  value={form.compatiblePlans}
                  onChange={(e) => setForm({ ...form, compatiblePlans: e.target.value })}
                  placeholder="KY_EDGE, KY_CORE"
                />
              </div>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400">
              El MXN es lo que cobra la plataforma hoy; el USD queda listo para el
              cobro vía Stripe (fase 2). Redondea el MXN a un precio comercial.
            </p>
          </div>
        )}
      </Modal>

      {/* Sub-modal: crear add-on */}
      <Modal
        title={`Nuevo add-on para ${ref ? formatRef(ref) : ''}`}
        open={creatingAddon}
        onCancel={() => setCreatingAddon(false)}
        onOk={handleCreateAddon}
        okText="Crear add-on (inactivo)"
        cancelText="Cancelar"
        confirmLoading={isSaving}
        destroyOnHidden
      >
        <div className="pt-4">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className={labelClass}>Código</label>
              <Input
                value={addonForm.addonCode}
                onChange={(e) => setAddonForm({ ...addonForm, addonCode: e.target.value })}
                placeholder="ej. BACKUP_WEEKLY"
              />
            </div>
            <div>
              <label className={labelClass}>Nombre</label>
              <Input
                value={addonForm.addonName}
                onChange={(e) => setAddonForm({ ...addonForm, addonName: e.target.value })}
                placeholder="ej. Backup semanal"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className={labelClass}>Descripción (opcional)</label>
            <Input
              value={addonForm.description}
              onChange={(e) => setAddonForm({ ...addonForm, description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className={labelClass}>Precio USD (maestro)</label>
              <InputNumber
                className="w-full"
                min={0}
                precision={2}
                value={addonForm.usd}
                onChange={(value) => setAddonForm({ ...addonForm, usd: value })}
              />
            </div>
            <div>
              <label className={labelClass}>Precio MXN (congelado)</label>
              <div className="flex gap-2">
                <InputNumber
                  className="flex-1"
                  min={0}
                  precision={2}
                  value={addonForm.mxn}
                  onChange={(value) => setAddonForm({ ...addonForm, mxn: value })}
                />
                <Tooltip title={fxRate ? `Calcular con 1 USD = $${fxRate} MXN` : 'Configura el tipo de cambio en Precios'}>
                  <button
                    onClick={() => {
                      if (addonForm.usd !== null && fxRate) {
                        setAddonForm({ ...addonForm, mxn: Math.round(addonForm.usd * fxRate) });
                      }
                    }}
                    disabled={!fxRate || addonForm.usd === null}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 disabled:opacity-40"
                  >
                    <CalculatorOutlined />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className="mb-2">
            <label className={labelClass}>Planes compatibles (vacío = todos)</label>
            <Select
              mode="multiple"
              className="w-full"
              value={addonForm.compatiblePlans}
              onChange={(value) => setAddonForm({ ...addonForm, compatiblePlans: value })}
              options={planCodeOptions}
              placeholder="Todos los planes de la familia"
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            El add-on nace inactivo; actívalo con el switch cuando esté listo.
          </p>
        </div>
      </Modal>
      {/* Sub-modal: crear plan desde cero */}
      <Modal
        title={`Nuevo plan para la familia ${product?.typeFamily ?? ''}`}
        open={creatingPlan}
        onCancel={() => setCreatingPlan(false)}
        onOk={handleCreatePlan}
        okText="Crear plan (inactivo)"
        cancelText="Cancelar"
        confirmLoading={isSaving}
        destroyOnHidden
      >
        <div className="pt-4">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className={labelClass}>Código del plan</label>
              <Input
                value={planForm.planCode}
                onChange={(e) => setPlanForm({ ...planForm, planCode: e.target.value })}
                placeholder="ej. KY_CORE"
              />
            </div>
            <div>
              <label className={labelClass}>Código de variante</label>
              <Input
                value={planForm.variantCode}
                onChange={(e) => setPlanForm({ ...planForm, variantCode: e.target.value })}
                placeholder="STANDARD"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className={labelClass}>Nombre del plan</label>
            <Input
              value={planForm.planName}
              onChange={(e) => setPlanForm({ ...planForm, planName: e.target.value })}
              placeholder="ej. Kubiy Core"
            />
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className={labelClass}>OS type</label>
              <Input
                value={planForm.osType}
                onChange={(e) => setPlanForm({ ...planForm, osType: e.target.value })}
                placeholder="GENERIC / LINUX / WINDOWS"
              />
            </div>
            <div>
              <label className={labelClass}>OS family (según lo pida el cliente)</label>
              <Input
                value={planForm.osFamily}
                onChange={(e) => setPlanForm({ ...planForm, osFamily: e.target.value })}
                placeholder="ej. SERVER, UBUNTU_22.04 — vacío si no aplica"
              />
            </div>
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-400 mb-4">
            Importante: OS type y OS family deben coincidir con lo que consulta la
            pantalla del cliente para esta familia (ej. APP_DEPLOY usa GENERIC +
            SERVER; EC2 usa LINUX + la familia del OS). Copia estos valores de un
            plan existente de la misma familia si hay duda.
          </p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className={labelClass}>Intervalo de cobro</label>
              <Select
                className="w-full"
                value={planForm.billingInterval}
                onChange={(value) => setPlanForm({ ...planForm, billingInterval: value })}
                options={['MONTH', 'YEAR', 'ONE_TIME'].map((v) => ({ value: v, label: v }))}
              />
            </div>
            <div>
              <label className={labelClass}>Modelo de cobro</label>
              <Select
                className="w-full"
                value={planForm.billingModel}
                onChange={(value) => setPlanForm({ ...planForm, billingModel: value })}
                options={['FIXED', 'PER_UNIT', 'FIXED_WITH_OVERAGE', 'TIERED'].map((v) => ({ value: v, label: v }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className={labelClass}>Precio USD (maestro)</label>
              <InputNumber
                className="w-full"
                min={0}
                precision={2}
                value={planForm.usd}
                onChange={(value) => setPlanForm({ ...planForm, usd: value })}
              />
            </div>
            <div>
              <label className={labelClass}>Precio MXN (congelado)</label>
              <div className="flex gap-2">
                <InputNumber
                  className="flex-1"
                  min={0}
                  precision={2}
                  value={planForm.mxn}
                  onChange={(value) => setPlanForm({ ...planForm, mxn: value })}
                />
                <Tooltip title={fxRate ? `Calcular con 1 USD = $${fxRate} MXN` : 'Configura el tipo de cambio en Precios'}>
                  <button
                    onClick={() => {
                      if (planForm.usd !== null && fxRate) {
                        setPlanForm({ ...planForm, mxn: Math.round(planForm.usd * fxRate) });
                      }
                    }}
                    disabled={!fxRate || planForm.usd === null}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 disabled:opacity-40"
                  >
                    <CalculatorOutlined />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
          <label className={labelClass}>Recursos incluidos (opcional)</label>
          <div className="grid grid-cols-3 gap-3 mb-2">
            <InputNumber
              className="w-full"
              min={0}
              value={planForm.vcpu}
              onChange={(value) => setPlanForm({ ...planForm, vcpu: value })}
              placeholder="vCPU"
            />
            <InputNumber
              className="w-full"
              min={0}
              value={planForm.memoryMb}
              onChange={(value) => setPlanForm({ ...planForm, memoryMb: value })}
              placeholder="RAM (MB)"
            />
            <InputNumber
              className="w-full"
              min={0}
              value={planForm.storageGb}
              onChange={(value) => setPlanForm({ ...planForm, storageGb: value })}
              placeholder="Disco (GB)"
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            El plan nace inactivo; actívalo con el switch cuando esté listo.
          </p>
        </div>
      </Modal>
    </>
  );
};

export default ProductPlansManager;
